import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Loader } from "@/components/Loader";

export interface InfiniteScrollIndicatorProps {
  /** Current loading state - determines which indicator to show */
  state: "loading-more";
  /** Optional message to display with the spinner (for internationalization support) */
  message?: string;
  /** Additional CSS classes */
  className?: string;
  /** Custom loading indicator (replaces default) */
  customIndicator?: ReactNode;
}

/**
 * InfiniteScrollIndicator
 *
 * Lightweight composition component for displaying loading states in infinite scroll contexts.
 * Uses the Loader component for consistency.
 *
 * - For initial loading: Use component-specific Skeleton implementations
 * - For loading-more: Use this component at the bottom of lists/tables
 *
 * By default, shows only a spinner (no text) to avoid internationalization concerns.
 * Consuming applications can provide their own localized message via the `message` prop.
 *
 * Example usage:
 * ```tsx
 * // Default: spinner only (no i18n needed)
 * <InfiniteScrollIndicator state="loading-more" />
 *
 * // With localized text
 * <InfiniteScrollIndicator
 *   state="loading-more"
 *   message={t('loading.loadMore')}
 * />
 * ```
 */
export function InfiniteScrollIndicator({ state, message, className, customIndicator }: InfiniteScrollIndicatorProps) {
  // Only handles loading-more state
  // Initial loading is handled by each component's custom skeleton
  if (state !== "loading-more") {
    return null;
  }

  const defaultIndicator = (
    <div
      className={twMerge("flex items-center justify-center gap-2 py-4", "text-text-secondary", className)}
      aria-label={message || "Loading"}
    >
      <Loader size={20} color="text-text-secondary" />
      {message && (
        <span className="text-sm" aria-hidden="true">
          {message}
        </span>
      )}
    </div>
  );

  return customIndicator || defaultIndicator;
}
